import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatGBP, formatDate } from "@/lib/api";
import { Briefcase, Users, CheckSquare, CurrencyGbp, ArrowRight, CalendarBlank } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";

const STATUS_LABELS = {
  intake: "Intake",
  in_progress: "In Progress",
  submitted: "Submitted",
  awaiting_decision: "Awaiting Decision",
  granted: "Granted",
  refused: "Refused",
  closed: "Closed",
};

function Kpi({ label, value, icon: Icon, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-slate-200 bg-white p-6 rounded-sm flex flex-col justify-between min-h-[130px]"
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
        <Icon size={18} weight="light" className="text-[#C5A059]" />
      </div>
      <div>
        <div className="text-3xl font-medium font-mono-num tracking-tight text-[#0B1528]">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setSummary(data));
  }, []);

  if (!summary) return <div className="text-sm text-slate-500">Loading dashboard…</div>;

  const statusData = Object.entries(summary.matter_status).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k, value: v,
  }));
  const typeData = Object.entries(summary.matter_type)
    .map(([k, v]) => ({ name: k, value: v }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  const barColors = ["#0B1528", "#1E293B", "#334155", "#475569", "#64748B", "#C5A059"];

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Overview</div>
        <h2 className="font-serif-brand text-3xl">Practice at a glance</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi testid="kpi-clients" label="Total Clients" value={summary.total_clients} icon={Users} />
        <Kpi testid="kpi-active-matters" label="Active Matters" value={summary.active_matters}
             sub={`${summary.total_matters} total`} icon={Briefcase} />
        <Kpi testid="kpi-tasks" label="Open Tasks" value={summary.tasks_due} icon={CheckSquare} />
        <Kpi testid="kpi-revenue" label="Revenue Billed" value={formatGBP(summary.revenue_billed)}
             sub={`${formatGBP(summary.outstanding)} outstanding`} icon={CurrencyGbp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-slate-200 bg-white rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Matter Distribution</div>
              <h3 className="text-lg font-medium mt-0.5">By status</h3>
            </div>
          </div>
          {statusData.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No matters yet — create your first one.</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData} margin={{ top: 8, right: 8, bottom: 8, left: -12 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="value" radius={0}>
                    {statusData.map((_, i) => (
                      <Cell key={i} fill={barColors[i % barColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="border border-slate-200 bg-white rounded-sm p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Upcoming</div>
          <h3 className="text-lg font-medium mt-0.5 mb-4">Deadlines</h3>
          {summary.upcoming_deadlines.length === 0 ? (
            <div className="text-sm text-slate-500 py-4">No upcoming deadlines.</div>
          ) : (
            <ul className="divide-y divide-slate-200 -mx-6">
              {summary.upcoming_deadlines.map((m) => (
                <li key={m.id} className="px-6 py-3 hover:bg-slate-50 transition-colors">
                  <Link to={`/matters/${m.id}`} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 font-mono-num">{m.reference}</div>
                      <div className="text-sm truncate">{m.title}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-1 text-xs text-[#9A3412]">
                        <CalendarBlank size={12} />
                        {formatDate(m.deadline)}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Caseload</div>
            <h3 className="text-lg font-medium mt-0.5">By matter type</h3>
          </div>
          <Link to="/matters" className="text-xs text-[#0B1528] hover:text-[#C5A059] inline-flex items-center gap-1">
            View all matters <ArrowRight size={12} />
          </Link>
        </div>
        {typeData.length === 0 ? (
          <div className="text-sm text-slate-500 py-6">No matters yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {typeData.map((t, i) => (
              <div key={t.name} className="border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] tracking-wider uppercase text-slate-500 truncate">{t.name}</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <div className="text-2xl font-medium font-mono-num text-[#0B1528]">{t.value}</div>
                  <div className="h-1 flex-1 bg-slate-100 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-[#C5A059]" style={{ width: `${Math.min(100, t.value * 15)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
